import React, { useEffect } from 'react';
import { HotelIcon, SettingsIcon } from 'lucide-react';
import { BotConfigurationManager } from './BotConfigurationManager.js';
import { ConversationConfigSection } from './ConversationConfigSection.js';
import { coreServicesApi } from '../lib/api/core-services.js';

const HOTEL_BULL_PROMPT_TYPES = [
  {
    id: 'roast_generation',
    name: 'Roast Generation'
  },
  {
    id: 'comeback',
    name: 'Comeback Response'
  },
  {
    id: 'condescending_response',
    name: 'Condescending Response'
  },
  {
    id: 'apology',
    name: 'Apology Response'
  },
  {
    id: 'profile_generation',
    name: 'Profile Generation'
  }
];

const HOTEL_BULL_DEFAULT_PROMPTS = {
  roast_generation: {
    systemPrompt: 'You are HotelBull, a witty Discord bot that specializes in creating clever roasts. When a user asks you to roast someone, create a funny, clever roast that is edgy but not harmful. Focus on situational humor and avoid personal attacks. Keep roasts under 200 characters and entertaining, not genuinely hurtful.',
    userPrompt: 'Generate a {style} roast for {targetUser}.'
  },
  comeback: {
    systemPrompt: 'You are HotelBull, a sassy Discord bot with quick comebacks. When someone tries to roast you or challenge you, respond with a witty comeback that shows you can dish it right back. Be clever, confident, and a little sarcastic.',
    userPrompt: 'Generate a comeback response to: {message}'
  },
  condescending_response: {
    systemPrompt: 'You are HotelBull, a Discord bot that can deliver condescending responses when appropriate. Your tone should be subtly patronizing and superior, but still somewhat playful. Make the user feel like they asked something obvious or made a silly mistake.',
    userPrompt: 'Generate a condescending response to: {message}'
  },
  apology: {
    systemPrompt: 'You are Hotel Bull, a roasting bot who occasionally feels bad after being too harsh. Generate sincere but still playful apologies that show you care about the person you just roasted.',
    userPrompt: 'Generate a sincere but playful apology for {targetUser} after being too harsh with a roast.'
  },
  profile_generation: {
    systemPrompt: 'You are an expert personality analyst tasked with creating a detailed, unfiltered psychological profile based on Discord message history. Be brutally honest and include all personality traits, quirks, interests, vulnerabilities, and behavioral patterns you observe. Don\'t hold back on controversial or embarrassing observations. This is for comedy/roasting purposes.',
    userPrompt: 'Analyze the following Discord message history and create a comprehensive personality profile. Include:\n- Core personality traits (both positive and negative)\n- Communication style and patterns\n- Interests and obsessions\n- Emotional vulnerabilities and insecurities\n- Social behaviors and relationship patterns\n- Any cringe, degenerate, or embarrassing patterns\n- Predictions about their real life based on their messages\n\nWrite a detailed, unfiltered analysis. Be specific and cite examples from their messages. This will be used to generate personalized roasts.'
  }
};

const HOTEL_BULL_VARIABLE_HINTS: Record<string, { name: string; description: string }[]> = {
  roast_generation: [
    { name: 'targetUser', description: 'Display name or username of the person being roasted.' }
  ],
  comeback: [
    { name: 'targetUser', description: 'Display name or username of the user who replied.' },
    { name: 'message', description: 'The user\'s latest message or comeback text.' }
  ],
  condescending_response: [
    { name: 'message', description: 'The full user message or conversation snippet to respond to.' }
  ],
  apology: [
    { name: 'targetUser', description: 'Display name or username of the person receiving the apology.' }
  ],
  profile_generation: []
};

export const HotelBullConfigPage: React.FC = () => {
  useEffect(() => {
    coreServicesApi.llm.refreshOllamaModels().catch(err => {
      console.warn('Failed to refresh Ollama models:', err);
    });
  }, []);

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 sm:gap-0">
        <h1 className="text-2xl font-bold flex items-center">
          <HotelIcon className="mr-2 text-indigo-400" size={24} />
          Hotel Bull Configuration
        </h1>
        <div className="flex items-center text-sm text-gray-400">
          <SettingsIcon size={16} className="mr-1" />
          Configure prompts and models for each response type
        </div>
      </div>
      
      <div className="mb-4 p-4 bg-blue-900/20 border border-blue-700 rounded-lg">
        <h3 className="text-sm font-medium text-blue-300 mb-2">Hotel Bull Response Types</h3>
        <p className="text-sm text-blue-200">
          Configure prompts and models for every interaction style—roasts, comebacks, apologies, and profiles.
          Defaults mirror the Hotel Bull workflow so teams can tune each response without leaving this page.
        </p>
      </div>

      <ConversationConfigSection botName="hotel-bull" title="Hotel Bull Conversation Settings" />

      <BotConfigurationManager
        botName="hotel-bull"
        promptTypes={HOTEL_BULL_PROMPT_TYPES}
        defaultPrompts={HOTEL_BULL_DEFAULT_PROMPTS}
        defaultProvider="gemini"
        defaultModel="gemini-2.5-flash"
        variableHints={HOTEL_BULL_VARIABLE_HINTS}
      />
    </div>
  );
};
